import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Container, Loader } from '@melio/penny';

import { completeSso } from '../../api/auth';
import { useAnalytics, type Cta, type PageName, type Flow, type MarketingRegisterSuccessEventName } from '../../analytics';
import { getEntryPoint, getIdentificationMethod } from '../../session';
import { completeAuthRedirect } from '../../hooks/useRedirectParam';
import {
  markSsoStateHandled,
  redirectSsoError,
  SSO_CODE_VERIFIER_KEY,
  SSO_RETURN_PATH_KEY,
  SSO_TAB_ID_KEY,
  wasSsoStateHandled,
} from '../../hooks/useSsoNavigation';
import { identifyUser, Logger } from '../../monitoring';
import { getSessionStorageItemSafely, isSessionStorageAvailable, removeSessionStorageItemSafely } from '../../utils/storage';
import { dismissDomainTransitionBanner } from '../DomainTransitionBanner/domainTransitionBannerCookie';

const RETURN_PATH_TO_PAGE_NAME: Record<string, PageName> = {
  '/sign-in': 'sign-in-to-melio',
  '/create-account': 'create-your-account',
};

const RETURN_PATH_TO_FLOW: Record<string, Flow> = {
  '/sign-in': 'sign-in',
  '/create-account': 'sign-up',
};

const getAnalyticsContext = (returnPath: string) => {
  const identificationMethod = getIdentificationMethod();

  const pageName: PageName = RETURN_PATH_TO_PAGE_NAME[returnPath] ?? 'sign-in-to-melio';
  const flow: Flow = RETURN_PATH_TO_FLOW[returnPath] ?? 'sign-in';

  const ctaByMethod: Record<string, Cta> = {
    google: 'sign-in-with-google',
    xero: 'sign-in-with-xero',
    intuit: 'sign-in-with-intuit',
  };
  const cta: Cta = (identificationMethod && ctaByMethod[identificationMethod]) ?? 'continue';

  const entryPoint = getEntryPoint();

  return { pageName, flow, entryPoint, identificationMethod, cta };
};

export const SsoCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const { track } = useAnalytics();

  useEffect(() => {
    const handleCallback = async () => {
      const ssoState = searchParams.get('sso_state');
      const error = searchParams.get('error');

      // Deliberately not scrubbed from the URL here: a reload/discarded-tab-restore/duplicate
      // during the async completeSso() window below is exactly the case the replay guard right
      // below needs to catch, and it can only do that by reading sso_state back out of the URL.
      // Scrubbing it early — even via the raw History API — mutates the address bar for real,
      // so a subsequent reload would hit bare /sso/callback with no sso_state to check against,
      // silently defeating the guard. Every terminal branch already does a full
      // window.location.replace(...), which clears sso_state from the URL/history for real once
      // we're actually done with it.
      if (ssoState && wasSsoStateHandled(ssoState)) {
        // This exact callback was already processed in this browsing context — a reload
        // during/after the original run (the async completeSso() window has no protection
        // today), a backgrounded tab the browser discarded and silently reloaded on refocus,
        // a duplicated/restored tab that inherited the sessionStorage snapshot, etc. Not a bug
        // needing Logger.error/Datadog noise, but the user still needs a normal, actionable
        // signal rather than silently landing back on sign-in with no explanation — show the
        // same "Sign-in failed" banner any other failure path uses, so they know to start over.
        //
        // Breadcrumb only (info, not error) — ssoTabId lets a later look correlate this against
        // the "SSO started" log for the same tab: present and matching means the guard fired in
        // the tab that started the flow; present but different, or absent entirely, means a
        // different browsing context (duplicated/restored tab) received this callback. hadError
        // records whether this replay arrived carrying a fresh provider/backend error param
        // (e.g. a second external redirect) rather than being a bare reload of the same URL.
        Logger.info('SSO callback replay detected', { ssoTabId: getSessionStorageItemSafely(SSO_TAB_ID_KEY), hadError: !!error });
        redirectSsoError('access_denied', 'sso_error', getSessionStorageItemSafely(SSO_RETURN_PATH_KEY) ?? '/sign-in');
        return;
      }
      if (ssoState) markSsoStateHandled(ssoState);

      const codeVerifier = getSessionStorageItemSafely(SSO_CODE_VERIFIER_KEY);
      const returnPath = getSessionStorageItemSafely(SSO_RETURN_PATH_KEY) ?? '/sign-in';
      removeSessionStorageItemSafely(SSO_CODE_VERIFIER_KEY);
      removeSessionStorageItemSafely(SSO_RETURN_PATH_KEY);

      const { pageName, flow, entryPoint, identificationMethod, cta } = getAnalyticsContext(returnPath);

      const trackStatus = (statusType: 'success' | 'failure') => {
        track({
          eventName: 'Connection-Status',
          properties: {
            PageName: pageName,
            Flow: flow,
            Intent: flow,
            EntryPoint: entryPoint,
            IdentificationMethod: identificationMethod,
            Cta: cta,
            StatusType: statusType,
          },
        });
      };

      if (error) {
        // provider_timeout is a provider-side gateway timeout (e.g. Intuit), not a Melio bug — same
        // non-actionable treatment as access_denied so it doesn't page as Datadog error-tracking noise.
        if (error !== 'access_denied' && error !== 'provider_timeout') {
          Logger.error('SSO callback received error from external-tokens', undefined, new Error(error));
        }
        trackStatus('failure');
        redirectSsoError('access_denied', 'sso_error', returnPath);
        return;
      }

      if (!ssoState || !codeVerifier) {
        Logger.error(
          'SSO callback missing required parameters',
          {
            hasSsoState: !!ssoState,
            hasCodeVerifier: !!codeVerifier,
            sessionStorageAvailable: isSessionStorageAvailable(),
            // Present but no codeVerifier => same/duplicated tab replaying an already-consumed
            // callback. Absent => this browsing context never ran startSso() at all (e.g. a
            // different tab picked up the redirect back from the IdP).
            ssoTabId: getSessionStorageItemSafely(SSO_TAB_ID_KEY),
          },
          new Error('SSO callback missing required parameters')
        );
        trackStatus('failure');
        redirectSsoError('access_denied', 'sso_error', returnPath);
        return;
      }

      const result = await completeSso({ ssoState, codeVerifier });

      if (result.type === 'emailNotVerified') {
        trackStatus('failure');
        redirectSsoError('access_denied', 'sso_email_not_verified', returnPath);
        return;
      }

      if (result.type === 'error') {
        Logger.error('SSO complete failed', undefined, new Error(result.message));
        trackStatus('failure');
        redirectSsoError('access_denied', 'sso_error', returnPath);
        return;
      }

      // Identify first so CCPA "Do Not Sell" DB opt-out gates the tracking calls below
      identifyUser(result.user);
      trackStatus('success');

      if (flow === 'sign-up' && identificationMethod) {
        const marketingEventByMethod: Partial<Record<string, MarketingRegisterSuccessEventName>> = {
          intuit: 'marketing_register_sign-up-with-intuit-success',
          xero: 'marketing_register_sign-up-with-xero-success',
          google: 'marketing_register_sign-up-with-google-success',
        };
        const marketingEvent = marketingEventByMethod[identificationMethod];
        if (marketingEvent) {
          track({ eventName: marketingEvent });
        }
      }

      dismissDomainTransitionBanner();
      completeAuthRedirect();
    };

    void handleCallback();
  }, [searchParams, track]);

  return (
    <Container
      data-testid="sso-callback-page"
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}
    >
      <Loader />
    </Container>
  );
};
